<template>
  <div id="page-plot-list">
    <div class="vx-card p-6">
      <div class="flex flex-wrap items-center mb-base">
        <div class="flex-grow">
          <vs-dropdown vs-trigger-click class="cursor-pointer">
            <div
              class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
            >
              <span class="mr-2"
                >{{ currentPageStart }}
                -
                {{ currentPageEnd }}
                / {{ totalPlots }}</span
              >
              <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
            </div>
            <vs-dropdown-menu>
              <vs-dropdown-item @click="onChangePageSize(10)">
                <span>10</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="onChangePageSize(20)">
                <span>20</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="onChangePageSize(30)">
                <span>30</span>
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </div>
      </div>
      <ag-grid-vue
        ref="agGridTable"
        class="ag-theme-material w-100 my-4 ag-grid-table"
        :components="components"
        :gridOptions="gridOptions"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="plots"
        rowSelection="multiple"
        colResizeDefault="shift"
        :animateRows="true"
        :floatingFilter="false"
        :pagination="true"
        :paginationPageSize="paginationPageSize"
        :suppressPaginationPanel="true"
        :suppressDragLeaveHidesColumns="true"
        :suppressNoRowsOverlay="true"
        @grid-ready="onGridReady"
        @rowClicked="onRowClicked"
      >
      </ag-grid-vue>
      <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
    </div>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import CellRendererLink from "./cell-renderer/CellRendererLink.vue";
import CellRendererStatus from "./cell-renderer/CellRendererStatus.vue";
import CellRendererVerified from "./cell-renderer/CellRendererVerified.vue";
import CellRendererActions from "./cell-renderer/CellRendererActions.vue";
import CellRendererPlotDate from "./cell-renderer/CellRendererPlotDate";
import CellRendererPlotCategory from "./cell-renderer/CellRendererPlotCategory";
import { mapGetters, createNamespacedHelpers } from "vuex";

const { mapActions } = createNamespacedHelpers("enterprisePlotList");
const INITIAL_PAGE_SIZE = 10;

export default {
  components: {
    AgGridVue,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererLink,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererStatus,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererVerified,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererActions,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererPlotDate,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererPlotCategory,
  },
  data() {
    return {
      searchQuery: "",
      searchName: "",
      gridOptions: {
        localeText: { noRowsToShow: "プロットが見つかりませんでした" },
        context: {
          componentParent: this,
        },
        rowData: null,
        columnDefs: null,
        onColumnResized: (params) => {
          console.log(params);
        },
      },
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMenu: true,
      },
      components: {
        CellRendererLink,
        CellRendererStatus,
        CellRendererVerified,
        CellRendererActions,
      },
      currentPage: 1,
      paginationPageSize: INITIAL_PAGE_SIZE, // 一覧に表示するレコードの数
    };
  },
  watch: {
    currentPage() {
      let page = this.currentPage - 1;
      this.searchPlots(page);
    },
  },
  computed: {
    ...mapGetters({
      plots: "enterprisePlot/getPlots",
      pageable: "enterprisePlot/getPageable",
      countryCodes: "constantResource/getCountryCodes",
    }),
    totalPages() {
      if (this.pageable.total_pages) return this.pageable.total_pages;
      else return 0;
    },
    totalPlots() {
      if (this.pageable) return this.pageable.total_elements;
      else return 0;
    },
    currentPageStart() {
      const pageStart =
        this.currentPage * this.paginationPageSize -
        (this.paginationPageSize - 1);
      return pageStart;
    },
    currentPageEnd() {
      const pageEnd =
        this.totalPlots - this.currentPage * this.paginationPageSize > 0
          ? this.currentPage * this.paginationPageSize
          : this.totalPlots;
      return pageEnd;
    },
  },
  methods: {
    ...mapActions(["resetPlotListState"]),

    async onGridReady() {
      await this.searchPlots();
      this.autoSizeAll();
    },

    onRowClicked(evt) {
      if (
        evt.event.target.classList.contains("vs-button") ||
        evt.event.target.classList.contains("evt-actions") ||
        evt.event.target.classList.contains("vs-button-text")
      ) {
        return;
      } else {
        this.$router.push({
          name: "enterprise-plot-edit",
          params: { id: evt.data.id },
        });
      }
    },
    async searchPlots(page) {
      this.$vs.loading();
      let request = {
        start: page,
        limit: this.paginationPageSize,
      };
      this.$store
        .dispatch("enterprisePlot/fetchPlots", request)
        .catch(() => {
          this.$vs.loading.close();
        })
        .finally(() => {
          this.$vs.loading.close();
        });
    },

    autoSizeAll() {
      let allColumnIds = [];
      this.gridColumnApi.getAllColumns().forEach(function (column) {
        allColumnIds.push(column.getId());
      });
      this.gridColumnApi.autoSizeColumns(allColumnIds, false);
    },

    cleanup() {
      this.resetPlotListState();
    },

    onChangePageSize(size) {
      this.paginationPageSize = size;
      this.currentPage = 1;
      this.searchPlots(0);
    },
    setColumnDefs() {
      this.columnDefs = [
        {
          headerName: "ID",
          field: "id",
        },
        {
          headerName: "プロット名",
          field: "title",
        },
        {
          headerName: "種別",
          field: "child_category.id",
          cellRenderer: "CellRendererPlotCategory",
        },
        {
          headerName: "コメント",
          field: "detail.comment",
        },
        {
          headerName: "表示開始日時(JST)",
          field: "detail.start_date",
          cellRenderer: "CellRendererPlotDate",
        },
        {
          headerName: "表示終了日時(JST)",
          field: "detail.end_date",
          cellRenderer: "CellRendererPlotDate",
        },
        {
          headerName: "作成日時(JST)",
          field: "created_at",
          cellRenderer: "CellRendererPlotDate",
        },
        {
          headerName: "更新日時(JST)",
          field: "updated_at",
          cellRenderer: "CellRendererPlotDate",
        },
        {
          headerName: "アクション",
          field: "id",
          cellRenderer: "CellRendererActions",
          cellClass: "actions",
        },
      ];
    },
  },
  async mounted() {
    let routerViewElement = document.getElementsByClassName("router-view");
    routerViewElement[0].style.padding = "2.2rem";

    this.gridColumnApi = this.gridOptions.columnApi;
  },
  async created() {},
  beforeMount() {
    this.setColumnDefs();
  },
  beforeDestroy() {
    this.cleanup();
  },
};
</script>

<style lang="scss">
#page-plot-list {
  .vx-input-group .prepend-text.btn-addon .vs-button {
    padding-top: -10px !important;
  }

  .vs-input-primary {
    width: 400px;
  }

  .plot-list-filters {
    .vs__actions {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-58%);
    }
  }

  .ag-grid-cell-chip {
    border-radius: 5px;

    &.vs-chip-success {
      background: rgba(var(--vs-success), 0.15);
      color: rgba(var(--vs-success), 1) !important;
      font-weight: 500;
    }

    &.vs-chip-warning {
      background: rgba(var(--vs-warning), 0.15);
      color: rgba(var(--vs-warning), 1) !important;
      font-weight: 500;
    }

    &.vs-chip-danger {
      background: rgba(var(--vs-danger), 0.15);
      color: rgba(var(--vs-danger), 1) !important;
      font-weight: 500;
    }
  }
}
</style>
